import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Phone, Link, UserCircle, Lock, Palette, Camera, EnvelopeSimple, ChartBar, CheckCircle, Users, Trash } from "@phosphor-icons/react";
import API from "../services/api";
import "./Settings.css";

const ACCENT_COLORS = [
  { label: "Teal",   value: "#3abb90" },
  { label: "Blue",   value: "#59B5F8" },
  { label: "Purple", value: "#b282f0" },
  { label: "Orange", value: "#EC6C21" },
  { label: "Pink",   value: "#e879a0" },
  { label: "Yellow", value: "#f59e0b" },
];

export default function Settings() {
  const { user, updateBranding, updateUser, branding } = useAuth();
  const [tab, setTab] = useState("profile");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const [twilio, setTwilio] = useState({ account_sid:"", auth_token:"", api_key:"", api_secret:"", twiml_app_sid:"", caller_id:"", tollfree_number:"" });
  const [zoho, setZoho] = useState({ client_id:"", client_secret:"", access_token:"", refresh_token:"" });
  const [profile, setProfile] = useState({ first_name:user?.first_name||"", last_name:user?.last_name||"", agent_identity:user?.agent_identity||"" });
  const [password, setPassword] = useState({ current_password:"", new_password:"", confirm_password:"" });
  const [team, setTeam] = useState([]);
  const [newAgent, setNewAgent] = useState({ first_name:"", last_name:"", email:"", password:"", agent_identity:"", extension:"" });
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [changingPassword, setChangingPassword] = useState(null); // agent id
  const [newPassword, setNewPassword] = useState("");
  const [smtp, setSmtp] = useState({ provider:"", host:"", port:587, secure:false, username:"", password:"", from_name:"Leovoid UCaaS", from_email:"" });
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [reports, setReports] = useState({ is_enabled:false, frequency:"daily", send_time:"08:00", recipients:[], sections:{ calls:true, leads:true, queue:true, history:true } });
  const [reportRecipient, setReportRecipient] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [weeklySending, setWeeklySending] = useState(false);
  const [brandingForm, setBrandingForm] = useState({
    accent_color: branding?.accent_color || "#3abb90",
    logo_collapsed: branding?.logo_collapsed || "",
    logo_full: branding?.logo_full || "",
    favicon: branding?.favicon || "",
  });
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || "");

  const avatarInputRef = useRef(null);
  const logoCollapsedRef = useRef(null);
  const logoFullRef = useRef(null);
  const faviconRef = useRef(null);

  useEffect(() => {
    API.get("/auth/team").then(({ data }) => { setTeam(data || []); }).catch(() => {});
    API.get("/smtp").then(({ data }) => {
      if (data.host) setSmtp(s => ({ ...s, ...data, password: "" }));
    }).catch(() => {});
    API.get("/reports/config").then(({ data }) => {
      if (data) setReports({...{is_enabled:false,frequency:"daily",send_time:"08:00",recipients:[],sections:{calls:true,leads:true,queue:true,history:true},timezone:"UTC",weekly_enabled:false,weekly_send_time:"08:00",weekly_recipients:[]},...data});
    }).catch(() => {});
    API.get("/twilio/credentials").then(({ data }) => {
      setTwilio(t => ({ ...t, account_sid:data.account_sid||"", api_key:data.api_key||"", twiml_app_sid:data.twiml_app_sid||"", caller_id:data.caller_id||"", tollfree_number:data.tollfree_number||"" }));
    }).catch(() => {});
  }, []);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type:"", text:"" }), 4000); };

  const toBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return showMsg("error", "Image must be under 2MB");
    setLoading(true);
    try {
      const b64 = await toBase64(file);
      setAvatarPreview(b64);
      const { data } = await API.patch("/settings/avatar", { avatar_url: b64 });
      const updatedUser = { ...user, avatar_url: b64 };
      updateUser(updatedUser);
      showMsg("success", "Profile photo updated.");
    } catch (err) {
      console.error("Avatar upload error:", err);
      showMsg("error", err.response?.data?.error || "Failed to upload photo");
    }
    setLoading(false);
  };

  const handleLogoChange = async (e, key) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return showMsg("error", "Image must be under 2MB");
    const b64 = await toBase64(file);
    setBrandingForm(f => ({ ...f, [key]: b64 }));
  };

  const saveTwilio = async (e) => { e.preventDefault(); setLoading(true); try { await API.post("/twilio/credentials", twilio); showMsg("success", "Twilio credentials saved."); } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); } setLoading(false); };
  const saveZoho = async (e) => { e.preventDefault(); setLoading(true); try { await API.post("/contacts/zoho-credentials", zoho); showMsg("success", "Zoho credentials saved."); } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); } setLoading(false); };
  const saveProfile = async (e) => { e.preventDefault(); setLoading(true); try { await API.patch("/settings/profile", profile); updateUser({ ...user, ...profile }); showMsg("success", "Profile updated."); } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); } setLoading(false); };
  const savePassword = async (e) => { e.preventDefault(); if (password.new_password !== password.confirm_password) return showMsg("error", "Passwords don't match"); setLoading(true); try { await API.patch("/settings/password", password); setPassword({ current_password:"", new_password:"", confirm_password:"" }); showMsg("success", "Password updated."); } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); } setLoading(false); };
  const saveBranding = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await API.patch("/settings/branding", brandingForm);
      updateBranding(brandingForm);
      showMsg("success", "Branding saved.");
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
    setLoading(false);
  };

  const changeAgentPassword = async (agentId) => {
    if (!newPassword || newPassword.length < 6) { showMsg("error", "Password must be at least 6 characters"); return; }
    try {
      await API.patch(`/auth/team/${agentId}/password`, { password: newPassword });
      showMsg("success", "Password updated successfully.");
      setChangingPassword(null);
      setNewPassword("");
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
  };

  const createAgent = async (e) => {
    e.preventDefault(); setCreatingAgent(true);
    try {
      await API.post("/auth/create-agent", newAgent);
      setNewAgent({ first_name:"", last_name:"", email:"", password:"", agent_identity:"" });
      const { data } = await API.get("/auth/team");
      setTeam(data || []);
      showMsg("success", "Agent account created.");
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
    setCreatingAgent(false);
  };

  const deleteAgent = async (id) => {
    if (!window.confirm("Delete this agent account?")) return;
    try {
      await API.delete(`/auth/team/${id}`);
      setTeam(t => t.filter(a => a.id !== id));
      showMsg("success", "Agent deleted.");
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
  };

  const SMTP_PROVIDERS = [
    { value: "",         label: "Custom / Other",  host: "",                    port: 587,  secure: false },
    { value: "gmail",    label: "Gmail",            host: "smtp.gmail.com",      port: 587,  secure: false },
    { value: "outlook",  label: "Outlook / Office 365", host: "smtp.office365.com", port: 587, secure: false },
    { value: "yahoo",    label: "Yahoo Mail",       host: "smtp.mail.yahoo.com", port: 465,  secure: true  },
    { value: "zoho",     label: "Zoho Mail",        host: "smtp.zoho.com",       port: 587,  secure: false },
    { value: "sendgrid", label: "SendGrid",         host: "smtp.sendgrid.net",   port: 587,  secure: false },
    { value: "mailgun",  label: "Mailgun",          host: "smtp.mailgun.org",    port: 587,  secure: false },
    { value: "ses",      label: "Amazon SES",       host: "email-smtp.us-east-1.amazonaws.com", port: 587, secure: false },
  ];

  const handleProviderChange = (value) => {
    const p = SMTP_PROVIDERS.find(p => p.value === value);
    if (p) setSmtp(s => ({ ...s, provider: value, host: p.host, port: p.port, secure: p.secure }));
  };

  const saveSmtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await API.post("/smtp", smtp);
      showMsg("success", "SMTP configuration saved.");
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
    setLoading(false);
  };

  const testSmtp = async () => {
    setSmtpTesting(true);
    try {
      const { data } = await API.post("/smtp/test");
      showMsg("success", data.message);
    } catch (err) { showMsg("error", err.response?.data?.error || "Test failed"); }
    setSmtpTesting(false);
  };

  const saveReports = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await API.post("/reports/config", reports);
      showMsg("success", "Report settings saved.");
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
    setLoading(false);
  };

  const sendWeeklyNow = async () => {
    setWeeklySending(true);
    try {
      const { data } = await API.post("/reports/send-weekly");
      showMsg("success", data.message);
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
    setWeeklySending(false);
  };

  const sendReportNow = async () => {
    setReportSending(true);
    try {
      const { data } = await API.post("/reports/send-now");
      showMsg("success", data.message);
    } catch (err) { showMsg("error", err.response?.data?.error || "Failed"); }
    setReportSending(false);
  };

  const addRecipient = () => {
    if (!reportRecipient || !reportRecipient.includes("@")) return;
    setReports(r => ({ ...r, recipients: [...(r.recipients||[]), reportRecipient] }));
    setReportRecipient("");
  };

  const TABS = [
    { id: "profile",  label: "Profile",  Icon: UserCircle },
    { id: "branding", label: "Branding", Icon: Palette },
    { id: "twilio",   label: "Twilio",   Icon: Phone },
    { id: "zoho",     label: "Zoho CRM", Icon: Link },
    { id: "security", label: "Security", Icon: Lock },
    { id: "smtp",     label: "SMTP Email", Icon: EnvelopeSimple },
    { id: "team",     label: "Team",       Icon: Users },
    { id: "reports",  label: "Reports",    Icon: ChartBar },
  ];

  return (
    <div className="settings-page">
      <div className="page-header">
        <div><h1 className="page-title">Settings</h1><p className="page-subtitle">Manage your account and integrations</p></div>
      </div>
      <div className="settings-layout">
        <div className="settings-nav">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} className={`settings-tab ${tab===id?"active":""}`} onClick={() => setTab(id)}>
              <Icon size={15}/> {label}
            </button>
          ))}
        </div>
        <div className="settings-content">
          {msg.text && <div className={msg.type==="success"?"msg-success":"msg-error"} style={{marginBottom:16}}>{msg.text}</div>}

          {/* PROFILE */}
          {tab==="profile" && (
            <div>
              <h2 className="settings-section-title">Profile</h2>
              {/* Avatar upload */}
              <div className="avatar-upload-section">
                <div className="avatar-upload-preview" onClick={() => avatarInputRef.current?.click()}>
                  {avatarPreview
                    ? <img src={avatarPreview} alt="avatar" style={{width:"100%",height:"100%",borderRadius:"50%",objectFit:"cover"}}/>
                    : <span style={{fontSize:28,fontWeight:700,color:"white"}}>{user?.avatar_initials||"?"}</span>
                  }
                  <div className="avatar-upload-overlay">
                    <Camera size={20} color="white"/>
                    <span>Change</span>
                  </div>
                </div>
                <div style={{flex:1}}>
                  <p style={{fontSize:13,color:"var(--color-gray)",marginBottom:8}}>Click the photo to upload a new profile picture.</p>
                  <p style={{fontSize:12,color:"var(--color-dark-gray)"}}>JPG, PNG or GIF · Max 2MB</p>
                </div>
                <input ref={avatarInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleAvatarChange}/>
              </div>
              <form onSubmit={saveProfile} style={{marginTop:20}}>
                <div className="form-row">
                  <div className="form-group"><label className="label">First Name</label><input value={profile.first_name} onChange={e=>setProfile(p=>({...p,first_name:e.target.value}))}/></div>
                  <div className="form-group"><label className="label">Last Name</label><input value={profile.last_name} onChange={e=>setProfile(p=>({...p,last_name:e.target.value}))}/></div>
                </div>
                <div className="form-group">
                  <label className="label">Agent Identity</label>
                  <input value={profile.agent_identity} onChange={e=>setProfile(p=>({...p,agent_identity:e.target.value.toLowerCase().replace(/\s/g,"_")}))} placeholder="john_smith"/>
                  <p className="input-hint">Used to identify you in Twilio calls. Lowercase, no spaces.</p>
                </div>
                <div className="form-group"><label className="label">Email</label><input value={user?.email||""} disabled/></div>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Saving...":"Save Profile"}</button>
              </form>
            </div>
          )}

          {/* BRANDING */}
          {tab==="branding" && (
            <form onSubmit={saveBranding}>
              <h2 className="settings-section-title">Branding</h2>
              <p className="settings-desc">Customize the look of your UCaaS platform. Changes are saved and applied on next login.</p>

              {/* Accent color */}
              <div className="form-group">
                <label className="label">Accent Color</label>
                <div className="color-swatches">
                  {ACCENT_COLORS.map(c => (
                    <button type="button" key={c.value}
                      className={`color-swatch ${brandingForm.accent_color===c.value?"selected":""}`}
                      style={{background:c.value}}
                      onClick={() => setBrandingForm(f=>({...f,accent_color:c.value}))}
                      title={c.label}/>
                  ))}
                  <input type="color" value={brandingForm.accent_color}
                    onChange={e=>setBrandingForm(f=>({...f,accent_color:e.target.value}))}
                    style={{width:36,height:36,padding:2,borderRadius:8,cursor:"pointer",border:"1px solid var(--color-surface-mid)"}}
                    title="Custom color"/>
                </div>
                <div style={{marginTop:8,display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:24,height:24,borderRadius:6,background:brandingForm.accent_color}}/>
                  <span style={{fontSize:12,color:"var(--color-gray)",fontFamily:"var(--font-mono)"}}>{brandingForm.accent_color}</span>
                </div>
              </div>

              {/* Logo collapsed */}
              <div className="form-group">
                <label className="label">Logo — Collapsed (Square, e.g. 40×40px)</label>
                <div className="logo-upload-row">
                  <div className="logo-preview-box square" onClick={() => logoCollapsedRef.current?.click()}>
                    {brandingForm.logo_collapsed
                      ? <img src={brandingForm.logo_collapsed} alt="collapsed logo" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                      : <span style={{fontSize:11,color:"var(--color-dark-gray)"}}>Click to upload</span>
                    }
                  </div>
                  <div>
                    <p style={{fontSize:12,color:"var(--color-gray)"}}>Shown when sidebar is collapsed.<br/>Recommended: 40×40px square PNG with transparent background.</p>
                    {brandingForm.logo_collapsed && <button type="button" className="btn btn-secondary btn-sm" style={{marginTop:8}} onClick={()=>setBrandingForm(f=>({...f,logo_collapsed:""}))}>Remove</button>}
                  </div>
                  <input ref={logoCollapsedRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleLogoChange(e,"logo_collapsed")}/>
                </div>
              </div>

              {/* Logo full */}
              <div className="form-group">
                <label className="label">Logo — Expanded (Wide, e.g. 160×40px)</label>
                <div className="logo-upload-row">
                  <div className="logo-preview-box wide" onClick={() => logoFullRef.current?.click()}>
                    {brandingForm.logo_full
                      ? <img src={brandingForm.logo_full} alt="full logo" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                      : <span style={{fontSize:11,color:"var(--color-dark-gray)"}}>Click to upload</span>
                    }
                  </div>
                  <div>
                    <p style={{fontSize:12,color:"var(--color-gray)"}}>Shown when sidebar is expanded.<br/>Recommended: 160×40px wide PNG with transparent background.</p>
                    {brandingForm.logo_full && <button type="button" className="btn btn-secondary btn-sm" style={{marginTop:8}} onClick={()=>setBrandingForm(f=>({...f,logo_full:""}))}>Remove</button>}
                  </div>
                  <input ref={logoFullRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleLogoChange(e,"logo_full")}/>
                </div>
              </div>

              {/* Favicon */}
              <div className="form-group">
                <label className="label">Favicon (Square, shown in browser tab)</label>
                <div className="logo-upload-row">
                  <div className="logo-preview-box favicon" onClick={() => faviconRef.current?.click()}>
                    {brandingForm.favicon
                      ? <img src={brandingForm.favicon} alt="favicon" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                      : <span style={{fontSize:11,color:"var(--color-dark-gray)"}}>Click to upload</span>
                    }
                  </div>
                  <div>
                    <p style={{fontSize:12,color:"var(--color-gray)"}}>Shown in the browser tab.<br/>Recommended: 32×32px square PNG or ICO.</p>
                    {brandingForm.favicon && <button type="button" className="btn btn-secondary btn-sm" style={{marginTop:8}} onClick={()=>setBrandingForm(f=>({...f,favicon:""}))}>Remove</button>}
                  </div>
                  <input ref={faviconRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleLogoChange(e,"favicon")}/>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Saving...":"Save Branding"}</button>
            </form>
          )}

          {/* TWILIO */}
          {tab==="twilio" && (
            <form onSubmit={saveTwilio}>
              <h2 className="settings-section-title">Twilio Credentials</h2>
              <div className="form-group"><label className="label">Account SID</label><input value={twilio.account_sid} onChange={e=>setTwilio(t=>({...t,account_sid:e.target.value}))} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" required/></div>
              <div className="form-group"><label className="label">Auth Token</label><input type="password" value={twilio.auth_token} onChange={e=>setTwilio(t=>({...t,auth_token:e.target.value}))} placeholder="••••••••" required/></div>
              <div className="form-row">
                <div className="form-group"><label className="label">API Key SID</label><input value={twilio.api_key} onChange={e=>setTwilio(t=>({...t,api_key:e.target.value}))} placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" required/></div>
                <div className="form-group"><label className="label">API Secret</label><input type="password" value={twilio.api_secret} onChange={e=>setTwilio(t=>({...t,api_secret:e.target.value}))} placeholder="••••••••" required/></div>
              </div>
              <div className="form-group"><label className="label">TwiML App SID</label><input value={twilio.twiml_app_sid} onChange={e=>setTwilio(t=>({...t,twiml_app_sid:e.target.value}))} placeholder="APxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" required/></div>
              <div className="form-row">
                <div className="form-group"><label className="label">Caller ID</label><input value={twilio.caller_id} onChange={e=>setTwilio(t=>({...t,caller_id:e.target.value}))} placeholder="+19177461556" required/></div>
                <div className="form-group"><label className="label">Toll-Free (optional)</label><input value={twilio.tollfree_number} onChange={e=>setTwilio(t=>({...t,tollfree_number:e.target.value}))} placeholder="+18887072570"/></div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Saving...":"Save & Verify"}</button>
            </form>
          )}

          {/* ZOHO */}
          {tab==="zoho" && (
            <form onSubmit={saveZoho}>
              <h2 className="settings-section-title">Zoho CRM Integration</h2>
              <div className="form-row">
                <div className="form-group"><label className="label">Client ID</label><input value={zoho.client_id} onChange={e=>setZoho(z=>({...z,client_id:e.target.value}))} placeholder="1000.xxxx" required/></div>
                <div className="form-group"><label className="label">Client Secret</label><input type="password" value={zoho.client_secret} onChange={e=>setZoho(z=>({...z,client_secret:e.target.value}))} placeholder="••••••••" required/></div>
              </div>
              <div className="form-group"><label className="label">Access Token</label><input type="password" value={zoho.access_token} onChange={e=>setZoho(z=>({...z,access_token:e.target.value}))} placeholder="••••••••" required/></div>
              <div className="form-group"><label className="label">Refresh Token</label><input type="password" value={zoho.refresh_token} onChange={e=>setZoho(z=>({...z,refresh_token:e.target.value}))} placeholder="••••••••" required/></div>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Saving...":"Save Zoho Credentials"}</button>
            </form>
          )}

          {/* TEAM */}
          {tab==="team" && (
            <div>
              <h2 className="settings-section-title">Team Members</h2>
              <p className="settings-desc">Create agent accounts. Agents can access all pages except Settings.</p>

              {/* Existing agents */}
              {team.length > 0 && (
                <div style={{marginBottom:24}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{borderBottom:"1px solid var(--color-surface-mid)"}}>
                        {["Name","Email","Agent Identity","Ext","Role",""].map(h=>(
                          <th key={h} style={{padding:"8px 12px",textAlign:"left",color:"var(--color-gray)",fontWeight:500,fontSize:11,textTransform:"uppercase"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {team.map(agent=>(
                        <tr key={agent.id} style={{borderBottom:"1px solid var(--color-surface)"}}>
                          <td style={{padding:"12px",color:"var(--color-white)",fontWeight:500}}>{agent.first_name} {agent.last_name}</td>
                          <td style={{padding:"12px",color:"var(--color-gray)"}}>{agent.email}</td>
                          <td style={{padding:"12px",fontFamily:"var(--font-mono)",fontSize:12,color:"var(--color-gray)"}}>{agent.agent_identity}</td>
                          <td style={{padding:"12px",fontFamily:"var(--font-mono)",fontSize:12,color:"var(--color-teal)"}}>{agent.extension||"—"}</td>
                          <td style={{padding:"12px"}}>
                            <span style={{background:agent.role==="admin"?"rgba(58,187,144,0.15)":"rgba(89,181,248,0.15)",color:agent.role==="admin"?"var(--color-teal)":"var(--color-blue)",padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:600}}>
                              {agent.role}
                            </span>
                          </td>
                          <td style={{padding:"12px"}}>
                            {agent.role !== "admin" && (
                              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                                {changingPassword === agent.id ? (
                                  <>
                                    <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)}
                                      placeholder="New password" style={{fontSize:12,padding:"4px 8px",width:130,borderRadius:6,border:"1px solid var(--color-surface-mid)",background:"var(--color-surface)",color:"var(--color-white)"}}
                                      onKeyDown={e=>{if(e.key==="Enter")changeAgentPassword(agent.id);if(e.key==="Escape"){setChangingPassword(null);setNewPassword("");}}}
                                      autoFocus/>
                                    <button onClick={()=>changeAgentPassword(agent.id)} className="btn btn-sm" style={{padding:"4px 10px",fontSize:11}}>Save</button>
                                    <button onClick={()=>{setChangingPassword(null);setNewPassword("");}} className="btn-icon" style={{color:"var(--color-gray)"}}>×</button>
                                  </>
                                ) : (
                                  <>
                                    <button onClick={()=>{setChangingPassword(agent.id);setNewPassword("");}} className="btn-icon" style={{color:"var(--color-blue)",fontSize:11,display:"flex",alignItems:"center",gap:3}}>
                                      <Lock size={13}/> Password
                                    </button>
                                    <button onClick={()=>deleteAgent(agent.id)} className="btn-icon" style={{color:"var(--color-danger)"}}>
                                      <Trash size={14}/>
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Create new agent */}
              <h3 style={{fontSize:14,fontWeight:600,color:"var(--color-white)",marginBottom:16}}>Create Agent Account</h3>
              <form onSubmit={createAgent}>
                <div className="form-row">
                  <div className="form-group"><label className="label">First Name</label><input required value={newAgent.first_name} onChange={e=>setNewAgent(a=>({...a,first_name:e.target.value}))}/></div>
                  <div className="form-group"><label className="label">Last Name</label><input required value={newAgent.last_name} onChange={e=>setNewAgent(a=>({...a,last_name:e.target.value}))}/></div>
                </div>
                <div className="form-group"><label className="label">Email</label><input type="email" required value={newAgent.email} onChange={e=>setNewAgent(a=>({...a,email:e.target.value}))}/></div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="label">Agent Identity</label>
                    <input required value={newAgent.agent_identity} onChange={e=>setNewAgent(a=>({...a,agent_identity:e.target.value.toLowerCase().replace(/\s/g,"_")}))} placeholder="john_smith"/>
                    <p className="input-hint">Lowercase, no spaces. Used for call routing.</p>
                  </div>
                  <div className="form-group">
                    <label className="label">Extension</label>
                    <input value={newAgent.extension||""} onChange={e=>setNewAgent(a=>({...a,extension:e.target.value}))} placeholder="1001"/>
                    <p className="input-hint">e.g. 1001, 1002. Used for direct extension routing.</p>
                  </div>
                  <div className="form-group"><label className="label">Password</label><input type="password" required value={newAgent.password} onChange={e=>setNewAgent(a=>({...a,password:e.target.value}))} minLength={8}/></div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={creatingAgent}>{creatingAgent?"Creating...":"Create Agent"}</button>
              </form>
            </div>
          )}

          {/* SMTP */}
          {tab==="smtp" && (
            <form onSubmit={saveSmtp}>
              <h2 className="settings-section-title">SMTP Email Configuration</h2>
              <p className="settings-desc">Configure your email server to send reports and notifications.</p>

              <div className="form-group">
                <label className="label">Email Provider</label>
                <select value={smtp.provider} onChange={e=>handleProviderChange(e.target.value)}>
                  {SMTP_PROVIDERS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {smtp.provider === "gmail" && (
                  <p className="input-hint">For Gmail, use an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{color:"var(--color-teal)"}}>App Password</a> instead of your regular password. Enable 2FA first.</p>
                )}
                {smtp.provider === "sendgrid" && (
                  <p className="input-hint">For SendGrid, use "apikey" as username and your API key as password.</p>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="label">SMTP Host</label>
                  <input value={smtp.host} onChange={e=>setSmtp(s=>({...s,host:e.target.value}))} placeholder="smtp.example.com" required/>
                </div>
                <div className="form-group" style={{maxWidth:120}}>
                  <label className="label">Port</label>
                  <input type="number" value={smtp.port} onChange={e=>setSmtp(s=>({...s,port:parseInt(e.target.value)}))} required/>
                </div>
                <div className="form-group" style={{maxWidth:120}}>
                  <label className="label">Encryption</label>
                  <select value={smtp.secure?"ssl":"tls"} onChange={e=>setSmtp(s=>({...s,secure:e.target.value==="ssl"}))}>
                    <option value="tls">TLS/STARTTLS</option>
                    <option value="ssl">SSL</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="label">Username / Email</label>
                  <input value={smtp.username} onChange={e=>setSmtp(s=>({...s,username:e.target.value}))} placeholder="you@example.com" required/>
                </div>
                <div className="form-group">
                  <label className="label">Password / App Password</label>
                  <input type="password" value={smtp.password} onChange={e=>setSmtp(s=>({...s,password:e.target.value}))} placeholder="••••••••" required/>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="label">From Name</label>
                  <input value={smtp.from_name} onChange={e=>setSmtp(s=>({...s,from_name:e.target.value}))} placeholder="Leovoid UCaaS"/>
                </div>
                <div className="form-group">
                  <label className="label">From Email</label>
                  <input type="email" value={smtp.from_email} onChange={e=>setSmtp(s=>({...s,from_email:e.target.value}))} placeholder="noreply@yourdomain.com" required/>
                </div>
              </div>

              <div style={{display:"flex",gap:10,marginTop:8}}>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Saving...":"Save SMTP"}</button>
                <button type="button" className="btn btn-secondary" onClick={testSmtp} disabled={smtpTesting}>{smtpTesting?"Sending...":"Send Test Email"}</button>
              </div>
            </form>
          )}

          {/* REPORTS */}
          {tab==="reports" && (
            <form onSubmit={saveReports}>
              <h2 className="settings-section-title">Automated Reports</h2>
              <p className="settings-desc">Send scheduled reports to your team via email. Requires SMTP to be configured.</p>

              <div className="form-group">
                <label className="label" style={{display:"flex",alignItems:"center",gap:10}}>
                  <input type="checkbox" checked={reports.is_enabled} onChange={e=>setReports(r=>({...r,is_enabled:e.target.checked}))} style={{width:"auto"}}/>
                  Enable Automated Reports
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="label">Frequency</label>
                  <select value={reports.frequency} onChange={e=>setReports(r=>({...r,frequency:e.target.value}))}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly (Monday)</option>
                    <option value="monthly">Monthly (1st)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Send Time</label>
                  <input type="time" value={reports.send_time} onChange={e=>setReports(r=>({...r,send_time:e.target.value}))}/>
                  <p className="input-hint">Server time (UTC)</p>
                </div>
              </div>

              <div className="form-group">
                <label className="label">Recipients</label>
                <div style={{display:"flex",gap:8,marginBottom:8}}>
                  <input value={reportRecipient} onChange={e=>setReportRecipient(e.target.value)}
                    placeholder="email@example.com" style={{flex:1}}
                    onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addRecipient();}}}/>
                  <button type="button" className="btn btn-secondary" onClick={addRecipient}>Add</button>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {(reports.recipients||[]).map((r,i)=>(
                    <span key={i} style={{display:"flex",alignItems:"center",gap:6,background:"var(--color-surface)",border:"1px solid var(--color-surface-mid)",borderRadius:20,padding:"4px 12px",fontSize:12}}>
                      {r}
                      <button type="button" onClick={()=>setReports(rep=>({...rep,recipients:rep.recipients.filter((_,j)=>j!==i)}))}
                        style={{background:"none",border:"none",color:"var(--color-danger)",cursor:"pointer",padding:0,fontSize:14,lineHeight:1}}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="label">Include in Report</label>
                <div style={{display:"flex",flexDirection:"column",gap:10,padding:"12px",background:"var(--color-surface)",borderRadius:8}}>
                  {[
                    {key:"calls",   label:"Call Summary (today totals, answer rate, missed)"},
                    {key:"leads",   label:"Lead Pipeline (status breakdown, pipeline value)"},
                    {key:"queue",   label:"Queue Stats (wait time, abandoned calls)"},
                    {key:"history", label:"Recent Calls (last 24h call log)"},
                  ].map(({key,label})=>(
                    <label key={key} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:13}}>
                      <input type="checkbox"
                        checked={reports.sections?.[key]||false}
                        onChange={e=>setReports(r=>({...r,sections:{...r.sections,[key]:e.target.checked}}))}
                        style={{width:"auto"}}/>
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Timezone */}
              <div className="form-group" style={{marginTop:8}}>
                <label className="label">Timezone</label>
                <select value={reports.timezone||"UTC"} onChange={e=>setReports(r=>({...r,timezone:e.target.value}))}>
                  {["UTC","America/New_York","America/Chicago","America/Denver","America/Los_Angeles","Europe/London","Europe/Paris","Europe/Berlin","Asia/Beirut","Asia/Dubai","Asia/Kolkata","Asia/Tokyo","Australia/Sydney"].map(tz=>(
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              {/* Weekly report */}
              <div className="form-group" style={{marginTop:8}}>
                <label className="label" style={{display:"flex",alignItems:"center",gap:10}}>
                  <input type="checkbox" checked={reports.weekly_enabled||false} onChange={e=>setReports(r=>({...r,weekly_enabled:e.target.checked}))} style={{width:"auto"}}/>
                  Enable Weekly Report (Friday to Monday)
                </label>
              </div>
              {reports.weekly_enabled && (
                <div style={{background:"var(--color-surface)",borderRadius:8,padding:16,marginTop:8}}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="label">Send Day</label>
                      <select value={reports.weekly_day||"1"} onChange={e=>setReports(r=>({...r,weekly_day:e.target.value}))}>
                        <option value="0">Sunday</option>
                        <option value="1">Monday</option>
                        <option value="2">Tuesday</option>
                        <option value="3">Wednesday</option>
                        <option value="4">Thursday</option>
                        <option value="5">Friday</option>
                        <option value="6">Saturday</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="label">Send Time</label>
                      <div style={{display:"flex",gap:8}}>
                        <select value={(reports.weekly_send_time||"08:00").split(":")[0]} onChange={e=>{const min=(reports.weekly_send_time||"08:00").split(":")[1]||"00";setReports(r=>({...r,weekly_send_time:`${e.target.value}:${min}`}))}}>
                          {Array.from({length:12},(_,i)=>String(i+1).padStart(2,"0")).map(h=>(
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                        <select value={(reports.weekly_send_time||"08:00").split(":")[1]||"00"} onChange={e=>{const hr=(reports.weekly_send_time||"08:00").split(":")[0]||"08";setReports(r=>({...r,weekly_send_time:`${hr}:${e.target.value}`}))}}>
                          <option value="00">:00</option>
                          <option value="15">:15</option>
                          <option value="30">:30</option>
                          <option value="45">:45</option>
                        </select>
                        <select value={parseInt((reports.weekly_send_time||"08:00").split(":")[0])>=12?"PM":"AM"} onChange={e=>{
                          const parts=(reports.weekly_send_time||"08:00").split(":");
                          let hr=parseInt(parts[0]);
                          if(e.target.value==="PM" && hr<12) hr+=12;
                          if(e.target.value==="AM" && hr>=12) hr-=12;
                          setReports(r=>({...r,weekly_send_time:`${String(hr).padStart(2,"0")}:${parts[1]||"00"}`}));
                        }}>
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="label">Weekly Recipients</label>
                    <div style={{display:"flex",gap:8,marginBottom:8}}>
                      <input
                        value={reports._weeklyRecipientInput||""}
                        onChange={e=>setReports(r=>({...r,_weeklyRecipientInput:e.target.value}))}
                        placeholder="email@example.com"
                        style={{flex:1}}
                        onKeyDown={e=>{
                          if(e.key==="Enter"){
                            e.preventDefault();
                            const v=(reports._weeklyRecipientInput||"").trim();
                            if(v&&v.includes("@")){
                              setReports(r=>({...r,weekly_recipients:[...(r.weekly_recipients||[]),v],_weeklyRecipientInput:""}));
                            }
                          }
                        }}
                      />
                      <button type="button" className="btn btn-secondary" onClick={()=>{
                        const v=(reports._weeklyRecipientInput||"").trim();
                        if(v&&v.includes("@")) setReports(r=>({...r,weekly_recipients:[...(r.weekly_recipients||[]),v],_weeklyRecipientInput:""}));
                      }}>Add</button>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {(reports.weekly_recipients||[]).map((r,i)=>(
                        <span key={i} style={{display:"flex",alignItems:"center",gap:6,background:"var(--color-surface-mid)",border:"1px solid var(--color-surface-mid)",borderRadius:20,padding:"4px 12px",fontSize:12}}>
                          {r}
                          <button type="button" onClick={()=>setReports(rep=>({...rep,weekly_recipients:rep.weekly_recipients.filter((_,j)=>j!==i)}))}
                            style={{background:"none",border:"none",color:"var(--color-danger)",cursor:"pointer",padding:0,fontSize:14,lineHeight:1}}>×</button>
                        </span>
                      ))}
                    </div>
                    <p className="input-hint">Leave empty to use same recipients as daily report</p>
                  </div>
                </div>
              )}

              <div style={{display:"flex",gap:10,marginTop:16,flexWrap:"wrap"}}>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Saving...":"Save Settings"}</button>
                <button type="button" className="btn btn-secondary" onClick={sendReportNow} disabled={reportSending}>{reportSending?"Sending...":"Test Daily Report"}</button>
                <button type="button" className="btn btn-secondary" onClick={sendWeeklyNow} disabled={weeklySending}>{weeklySending?"Sending...":"Test Weekly Report"}</button>
              </div>

              {reports.last_sent_at && (
                <p style={{marginTop:12,fontSize:12,color:"var(--color-gray)"}}>
                  Last report sent: {new Date(reports.last_sent_at).toLocaleString()}
                </p>
              )}
            </form>
          )}

          {/* SECURITY */}
          {tab==="security" && (
            <form onSubmit={savePassword}>
              <h2 className="settings-section-title">Change Password</h2>
              <div className="form-group"><label className="label">Current Password</label><input type="password" value={password.current_password} onChange={e=>setPassword(p=>({...p,current_password:e.target.value}))} required/></div>
              <div className="form-group"><label className="label">New Password</label><input type="password" value={password.new_password} onChange={e=>setPassword(p=>({...p,new_password:e.target.value}))} required minLength={8}/></div>
              <div className="form-group"><label className="label">Confirm Password</label><input type="password" value={password.confirm_password} onChange={e=>setPassword(p=>({...p,confirm_password:e.target.value}))} required/></div>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Saving...":"Update Password"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
